/* Relatórios de atendimento: contrato da API, consulta, tempo legível e filtros salvos.

   Tudo o que a tela de relatórios sabe sobre a API mora aqui. A exportação CSV não passa por
   `fetch`: é um endereço que o navegador baixa com o cookie de sessão, e o Excel recebe o arquivo
   como a API o gerou (UTF-8 com BOM e `;`). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BASE_DA_API, apagar, enviarJson, pedirJson } from "./api";

const RAIZ = "/relatorios/atendimento";

export const DIMENSOES = [
  ["organizacao", "Empresa"],
  ["servico", "Serviço"],
  ["equipe", "Equipe"],
  ["responsavel", "Responsável"],
  ["urgencia", "Urgência"],
] as const;
export type Dimensao = (typeof DIMENSOES)[number][0];
const esquemaDimensao = z.enum(["organizacao", "servico", "equipe", "responsavel", "urgencia"]);

const esquemaLinha = z.object({
  chave: z.string().nullable(),
  rotulo: z.string(),
  backlog_no_inicio: z.number().int(),
  backlog_no_fim: z.number().int(),
  abertos: z.number().int(),
  resolvidos: z.number().int(),
  primeira_resposta_medida: z.number().int(),
  primeira_resposta_media_segundos: z.number().int().nullable(),
  primeira_resposta_mediana_segundos: z.number().int().nullable(),
  solucao_medida: z.number().int(),
  solucao_media_segundos: z.number().int().nullable(),
  solucao_mediana_segundos: z.number().int().nullable(),
  reabertos: z.number().int(),
  taxa_de_reabertura_percentual: z.number().nullable(),
  resolvidos_de_primeira: z.number().int(),
  fcr_percentual: z.number().nullable(),
  sla_primeira_resposta_medida: z.number().int(),
  sla_primeira_resposta_no_prazo: z.number().int(),
  sla_primeira_resposta_percentual: z.number().nullable(),
  sla_solucao_medida: z.number().int(),
  sla_solucao_no_prazo: z.number().int(),
  sla_solucao_percentual: z.number().nullable(),
});

const esquemaIndicadores = z.object({
  inicio: z.string(),
  fim: z.string(),
  agrupar_por: esquemaDimensao.nullable(),
  total: esquemaLinha,
  linhas: z.array(esquemaLinha),
});

const esquemaPermanencia = z.object({
  inicio: z.string(),
  fim: z.string(),
  por: z.enum(["estado", "responsavel"]),
  linhas: z.array(
    z.object({
      chave: z.string(),
      rotulo: z.string(),
      chamados: z.number().int(),
      total_segundos: z.number().int(),
      media_segundos: z.number().int(),
    }),
  ),
});

const esquemaListaDoRelatorio = z.object({
  metrica: z.string(),
  total: z.number().int(),
  chamados: z.array(
    z.object({
      id: z.string().uuid(),
      numero: z.number().int(),
      assunto: z.string(),
      organizacao_id: z.string().uuid(),
      organizacao: z.string(),
      servico: z.string().nullable(),
      urgencia: z.string(),
      estado: z.string(),
      equipe_id: z.string().uuid().nullable(),
      responsavel_id: z.string().uuid().nullable(),
      criado_em: z.string(),
      resolvido_em: z.string().nullable(),
      valor_segundos: z.number().int().nullable(),
    }),
  ),
});

const esquemaOpcao = z.object({ id: z.string().uuid(), nome: z.string() });
const esquemaOpcoes = z.object({
  organizacoes: z.array(esquemaOpcao),
  servicos: z.array(z.string()),
  equipes: z.array(esquemaOpcao),
  agentes: z.array(esquemaOpcao),
});

const esquemaParametrosDoFiltro = z.object({
  inicio: z.string().nullish(),
  fim: z.string().nullish(),
  ultimos_dias: z.number().int().nullish(),
  organizacao_id: z.string().nullish(),
  servico: z.string().nullish(),
  equipe_id: z.string().nullish(),
  responsavel_id: z.string().nullish(),
  urgencia: z.string().nullish(),
  agrupar_por: esquemaDimensao.nullish(),
});
const esquemaFiltroSalvo = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  relatorio: z.enum(["indicadores", "permanencia"]),
  parametros: esquemaParametrosDoFiltro,
  criado_em: z.string(),
});

export type LinhaDeIndicadores = z.infer<typeof esquemaLinha>;
export type RelatorioDeIndicadores = z.infer<typeof esquemaIndicadores>;
export type RelatorioDePermanencia = z.infer<typeof esquemaPermanencia>;
export type ListaDoRelatorio = z.infer<typeof esquemaListaDoRelatorio>;
export type OpcoesDoRelatorio = z.infer<typeof esquemaOpcoes>;
export type FiltroSalvo = z.infer<typeof esquemaFiltroSalvo>;
export type ParametrosDoFiltro = z.infer<typeof esquemaParametrosDoFiltro>;

/** O recorte da tela. Texto vazio = sem restrição. */
export interface FiltrosDoRelatorio {
  inicio: string;
  fim: string;
  organizacao_id: string;
  servico: string;
  equipe_id: string;
  responsavel_id: string;
  urgencia: string;
  agrupar_por: Dimensao | "";
}

/** O que o servidor lista quando se abre um número: o indicador e, se for o caso, a linha. */
export interface SelecaoDeLista {
  metrica: string;
  /** Valor do grupo (ou do estado/responsável, na permanência); ausente = o total. */
  chave?: string;
  /** Como a tela chama esta lista. */
  titulo: string;
}

export const ROTULO_DA_METRICA: Record<string, string> = {
  backlog_no_inicio: "Backlog no início do período",
  backlog_no_fim: "Backlog no fim do período",
  abertos: "Abertos no período",
  resolvidos: "Resolvidos no período",
  primeira_resposta: "Com primeira resposta medida",
  solucao: "Com solução medida",
  reabertos: "Reabertos depois de resolvidos",
  resolvidos_de_primeira: "Resolvidos no primeiro contato",
  sla_primeira_resposta_no_prazo: "Primeira resposta dentro do prazo",
  sla_primeira_resposta_fora: "Primeira resposta fora do prazo",
  sla_solucao_no_prazo: "Solução dentro do prazo",
  sla_solucao_fora: "Solução fora do prazo",
  permanencia_estado: "Tempo no estado",
  permanencia_responsavel: "Tempo com o responsável",
};

const CHAVES_DO_RECORTE = [
  "inicio",
  "fim",
  "organizacao_id",
  "servico",
  "equipe_id",
  "responsavel_id",
  "urgencia",
  "agrupar_por",
] as const;

/** A query string do recorte, sem os campos vazios. `extras` (indicador, chave, `por`) vai junto. */
export function consultaDoRelatorio(
  filtros: FiltrosDoRelatorio,
  extras: Record<string, string | undefined> = {},
): string {
  const parametros = new URLSearchParams();
  for (const chave of CHAVES_DO_RECORTE) {
    if (filtros[chave]) parametros.set(chave, filtros[chave]);
  }
  for (const [chave, valor] of Object.entries(extras)) {
    if (valor) parametros.set(chave, valor);
  }
  return parametros.toString();
}

function comConsulta(caminho: string, consulta: string): string {
  return consulta ? `${caminho}?${consulta}` : caminho;
}

/** Endereço que o navegador baixa: `tipo` é `indicadores`, `permanencia` ou `chamados`. */
export function enderecoDoCsv(
  tipo: "indicadores" | "permanencia" | "chamados",
  filtros: FiltrosDoRelatorio,
  extras: Record<string, string | undefined> = {},
): string {
  return comConsulta(`${BASE_DA_API}${RAIZ}/${tipo}/csv`, consultaDoRelatorio(filtros, extras));
}

/** A data civil de hoje em Brasília, `AAAA-MM-DD` — o dia de corte do servidor. */
export function hojeEmBrasilia(agora: Date = new Date()): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(agora);
}

/** Soma (ou subtrai) dias de uma data `AAAA-MM-DD`, sem passar por fuso. */
export function somarDias(dia: string, dias: number): string {
  const data = new Date(`${dia}T00:00:00Z`);
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString().slice(0, 10);
}

export function periodoDosUltimosDias(dias: number, agora: Date = new Date()) {
  const fim = hojeEmBrasilia(agora);
  return { inicio: somarDias(fim, -(dias - 1)), fim };
}

export function filtrosIniciais(agora: Date = new Date()): FiltrosDoRelatorio {
  return {
    ...periodoDosUltimosDias(30, agora),
    organizacao_id: "",
    servico: "",
    equipe_id: "",
    responsavel_id: "",
    urgencia: "",
    agrupar_por: "",
  };
}

/** Quantos dias o período cobre, contando as duas pontas (0 quando as datas não fecham). */
export function diasDoPeriodo(inicio: string, fim: string): number {
  if (!inicio || !fim) return 0;
  const dias =
    (Date.parse(`${fim}T00:00:00Z`) - Date.parse(`${inicio}T00:00:00Z`)) / 86_400_000 + 1;
  return Number.isFinite(dias) && dias > 0 ? dias : 0;
}

/** Reabre um filtro salvo. Período móvel ("últimos N dias") vale a partir de hoje, não de quando foi salvo. */
export function filtrosDoSalvo(
  parametros: ParametrosDoFiltro,
  agora: Date = new Date(),
): FiltrosDoRelatorio {
  const base = filtrosIniciais(agora);
  const periodo = parametros.ultimos_dias
    ? periodoDosUltimosDias(parametros.ultimos_dias, agora)
    : { inicio: parametros.inicio ?? base.inicio, fim: parametros.fim ?? base.fim };
  return {
    ...periodo,
    organizacao_id: parametros.organizacao_id ?? "",
    servico: parametros.servico ?? "",
    equipe_id: parametros.equipe_id ?? "",
    responsavel_id: parametros.responsavel_id ?? "",
    urgencia: parametros.urgencia ?? "",
    agrupar_por: parametros.agrupar_por ?? "",
  };
}

/** O que vai para o banco: só o preenchido; período móvel troca as datas fixas. */
export function parametrosParaSalvar(
  filtros: FiltrosDoRelatorio,
  periodoMovel: boolean,
): Record<string, string | number> {
  const parametros: Record<string, string | number> = {};
  for (const chave of CHAVES_DO_RECORTE) {
    if (filtros[chave] && !(periodoMovel && (chave === "inicio" || chave === "fim"))) {
      parametros[chave] = filtros[chave];
    }
  }
  if (periodoMovel) parametros.ultimos_dias = diasDoPeriodo(filtros.inicio, filtros.fim);
  return parametros;
}

/** Duração em horas e minutos ("39 h 00 min"); `—` quando não há medida. */
export function formatarDuracao(segundos: number | null | undefined): string {
  if (segundos == null) return "—";
  if (segundos < 60) return `${segundos} s`;
  const minutos = Math.round(segundos / 60);
  const horas = Math.floor(minutos / 60);
  const resto = String(minutos % 60).padStart(2, "0");
  return horas > 0 ? `${horas} h ${resto} min` : `${minutos} min`;
}

export function formatarPercentual(valor: number | null | undefined): string {
  return valor == null ? "—" : `${valor.toLocaleString("pt-BR", { minimumFractionDigits: 1 })}%`;
}

/* ------------------------------------------------------------------ consultas */

const CHAVE = ["relatorios", "atendimento"] as const;

export function useOpcoesDoRelatorio(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE, "opcoes"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson(`${RAIZ}/opcoes`, esquemaOpcoes, { signal }),
  });
}

export function useIndicadoresDeAtendimento(filtros: FiltrosDoRelatorio, enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "indicadores", filtros],
    queryFn: ({ signal }) =>
      pedirJson(
        comConsulta(`${RAIZ}/indicadores`, consultaDoRelatorio(filtros)),
        esquemaIndicadores,
        { signal },
      ),
    enabled,
  });
}

export function usePermanenciaDeAtendimento(
  filtros: FiltrosDoRelatorio,
  por: "estado" | "responsavel",
  enabled = true,
) {
  return useQuery({
    queryKey: [...CHAVE, "permanencia", por, filtros],
    queryFn: ({ signal }) =>
      pedirJson(
        comConsulta(`${RAIZ}/permanencia`, consultaDoRelatorio({ ...filtros, agrupar_por: "" }, { por })),
        esquemaPermanencia,
        { signal },
      ),
    enabled,
  });
}

/** A lista de chamados de um número. Sem seleção, não consulta. */
export function useChamadosDoRelatorio(
  filtros: FiltrosDoRelatorio,
  selecao: SelecaoDeLista | null,
) {
  return useQuery({
    queryKey: [...CHAVE, "chamados", filtros, selecao],
    queryFn: ({ signal }) =>
      pedirJson(
        comConsulta(`${RAIZ}/chamados`, consultaDaLista(filtros, selecao!)),
        esquemaListaDoRelatorio,
        { signal },
      ),
    enabled: selecao !== null,
  });
}

/** Os parâmetros de uma lista: o recorte da tela mais o indicador e o valor da linha aberta. */
export function consultaDaLista(filtros: FiltrosDoRelatorio, selecao: SelecaoDeLista): string {
  const daPermanencia = selecao.metrica.startsWith("permanencia_");
  return consultaDoRelatorio(daPermanencia ? { ...filtros, agrupar_por: "" } : filtros, {
    metrica: selecao.metrica,
    chave: selecao.chave,
  });
}

export function useFiltrosSalvos() {
  return useQuery({
    queryKey: [...CHAVE, "filtros"],
    queryFn: ({ signal }) => pedirJson(`${RAIZ}/filtros`, z.array(esquemaFiltroSalvo), { signal }),
  });
}

export function useSalvarFiltroDeRelatorio() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      nome: string;
      relatorio: "indicadores" | "permanencia";
      parametros: Record<string, string | number>;
    }) => enviarJson(`${RAIZ}/filtros`, esquemaFiltroSalvo, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "filtros"] }),
  });
}

export function useApagarFiltroDeRelatorio() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`${RAIZ}/filtros/${encodeURIComponent(id)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "filtros"] }),
  });
}
